import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { messengerService } from '../functions/messenger';
import { useLocation } from 'react-router-dom';

const MessageNotificationContext = createContext();

export const useMessageNotifications = () => {
  const context = useContext(MessageNotificationContext);
  if (!context) {
    throw new Error('useMessageNotifications must be used within a MessageNotificationProvider');
  }
  return context;
};

export const MessageNotificationProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  const user = useSelector((state) => state.user);
  const location = useLocation();

  // Badge count = unread chats count (each chat contributes max 1).
  const unreadMessageCount = useMemo(
    () => conversations.filter((conversation) => (Number(conversation?.unreadCount) || 0) > 0).length,
    [conversations]
  );

  // Initialize socket connection from notification context if available
  useEffect(() => {
    if (user?.id && window.notificationSocket) {
      setSocket(window.notificationSocket);
      setIsConnected(window.notificationSocket.connected);
    }
  }, [user?.id]);

  // Fetch initial conversations (unread count derived from conversations)
  useEffect(() => {
    const fetchConversations = async () => {
      if (user?.token) {
        try {
          const conversationsData = await messengerService.getConversations(user.token);
          setConversations(Array.isArray(conversationsData) ? conversationsData : []);
        } catch (error) {
          console.error('Error fetching conversations for notifications:', error);
        }
      }
    };
    fetchConversations();
  }, [user?.token]);

  // Socket event listeners for message notifications
  useEffect(() => {
    if (!socket || !user?.id) return;

    // Listen for new messages
    const handleNewMessage = ({ message, conversationId }) => {
      console.log("🔔 MessageNotificationContext received new_message:", { 
        messageId: message?._id, 
        conversationId, 
        messageType: message?.type,
        sender: message?.sender?.first_name
      });
      
      // Only handle if we have user data
      if (!user?.id) return;
      
      // Only count if message is not from current user so the badge stays in sync in real time.
      if (message.sender._id !== user.id) {
        setConversations(prev => {
          const existing = prev.find(c => c._id === conversationId);
          if (existing) {
            return prev.map(conv =>
              conv._id === conversationId
                ? { ...conv, lastMessage: message, lastActivity: new Date(), unreadCount: (conv.unreadCount || 0) + 1 }
                : conv
            );
          }
          // New conversation: add so badge count updates in real time
          return [...prev, { _id: conversationId, lastMessage: message, lastActivity: new Date(), unreadCount: 1 }];
        });

        // Show browser notification if permission is granted
        if (Notification.permission === 'granted') {
          new Notification(`New message from ${message.sender.first_name} ${message.sender.last_name}`, {
            body: message.type === 'text' ? message.content.text : `Sent a ${message.type}`,
            icon: message.sender.picture || '/images/default_pic.png',
            tag: `message_${conversationId}`,
            requireInteraction: false
          });
        }
      }
    };

    // Listen for messages being marked as read (real-time)
    const handleMessagesRead = ({ conversationId, readBy }) => {
      if (readBy === user.id) return;
      setConversations(prev => prev.map(conv =>
        conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
      ));
    };

    // Keep the cached conversations preview in sync when a message is
    // deleted for everyone — if it was the last message in a chat, surface
    // "This message was deleted" wherever this context is consumed.
    const handleMessageDeleted = ({ messageId, deleteForEveryone }) => {
      if (!deleteForEveryone) return;
      setConversations(prev => prev.map(conv => (
        conv.lastMessage && conv.lastMessage._id === messageId
          ? {
              ...conv,
              lastMessage: {
                ...conv.lastMessage,
                deleted: {
                  ...(conv.lastMessage.deleted || {}),
                  isDeleted: true,
                  deletedAt: new Date(),
                },
              },
            }
          : conv
      )));
    };

    socket.on('new_message', handleNewMessage);
    socket.on('messages_read', handleMessagesRead);
    socket.on('message_deleted', handleMessageDeleted);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('messages_read', handleMessagesRead);
      socket.off('message_deleted', handleMessageDeleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.id, location.pathname]);

  // Mark conversation as read
  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!user?.token) return;
    try {
      await messengerService.markAsRead(conversationId, user.token);
      setConversations(prev => prev.map(conv =>
        conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
      ));
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, [user?.token]);

  // Reset unread count (useful when user opens messenger)
  const resetUnreadCount = useCallback(() => {
    setConversations(prev => prev.map(conv => ({ ...conv, unreadCount: 0 })));
  }, []);

  // Listen for local read events (real-time when user opens a conversation and markAsRead is called)
  useEffect(() => {
    const handleLocalRead = (e) => {
      const conversationId = e?.detail?.conversationId;
      if (!conversationId) return;
      setConversations(prev => prev.map(conv =>
        conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
      ));
    };
    window.addEventListener('messagesReadLocal', handleLocalRead);
    return () => window.removeEventListener('messagesReadLocal', handleLocalRead);
  }, []);

  // Memoize the context value
  const value = useMemo(() => ({
    unreadMessageCount,
    conversations,
    markConversationAsRead,
    resetUnreadCount,
    isConnected
  }), [
    unreadMessageCount,
    conversations,
    markConversationAsRead,
    resetUnreadCount,
    isConnected
  ]);

  return (
    <MessageNotificationContext.Provider value={value}>
      {children}
    </MessageNotificationContext.Provider>
  );
}; 
